import * as React from "react";
import { observer } from "mobx-react-lite";
import ShiftThemeContainer from "../../shift/ShiftThemeContainer";
import { useWeekdaySelection, useKeyboardNavigation, useHoverFocus } from "./hooks";
import ISmartScheduleShiftEntity, { WeekDays } from "sh-models/shift/ISmartScheduleShiftEntity";
import { ScheduleCellRenderSize } from "../../../../StaffHubConstants";
import Shift from "../../shift/Shift";
import { SlotSelector } from "../../shift/editorContentShift/SlotSelector";
import {
    Checkbox,
    CheckboxOnChangeData,
    Fluent9Text as Text,
    Tooltip,
    Button,
    DeleteRegular,
    EditRegular
} from "@microsoft/shifts-ui-core";
import {
    IDropdownOption,
    IToggle,
    } from "@fluentui/react";
import AutomationUtils from "sh-application/utility/AutomationUtil";
import ShiftUtils from "sh-application/utility/ShiftUtils";
import {
    MAXIMUM_OPEN_SHIFT_SLOTS,
    MINIMUM_OPEN_SHIFT_SLOTS
    } from "sh-application/../StaffHubConstants";
import StringsStore from "../../../../sh-strings/store";
import { scheduleGenerationEditorState } from "../store/store";
import { deleteShiftBlock, updateSlotsSmartScheduleShiftById } from "../../smartSchedule/mutatorActions/actions";
import { launchShiftEditor, shiftEditorViewState } from "../../shift/lib";
import { ScheduleCalendarTypes, ShiftTypes } from "../../../../sh-models";
import { useStyles } from "./ShiftBlock.styles";
import { getStrings } from "@microsoft/shifts-experiences-shared";
const classNames = require("classnames/bind");

const memoize = UNIT_TEST_MODE ? require('memoize-one') : require('memoize-one').default;

interface IProps {
    shift: ISmartScheduleShiftEntity;
    onDeleted?: () => void;
}

const ShiftBlock: React.FC<IProps> = (props: IProps) => {
    const _strings = StringsStore().registeredStringModules.get("addEditShiftTimeOffPanelFullPage").strings;
    const _weekStrings = StringsStore().registeredStringModules.get("dayOfWeek").strings;

    const WEEKDAYS_MAP = new Map<string, WeekDays>([
        [_weekStrings.get("sunday"), WeekDays.Sunday],
        [_weekStrings.get("monday"), WeekDays.Monday],
        [_weekStrings.get("tuesday"), WeekDays.Tuesday],
        [_weekStrings.get("wednesday"), WeekDays.Wednesday],
        [_weekStrings.get("thursday"), WeekDays.Thursday],
        [_weekStrings.get("friday"), WeekDays.Friday],
        [_weekStrings.get("saturday"), WeekDays.Saturday]
    ]);
    const { shift } = props;
    const containerRef = React.useRef<HTMLDivElement>(null);
    const weekdayRefs = React.useRef<(HTMLDivElement | null)[]>([]);
    const styles = useStyles();

    // Use weekday selection hook
    const {
        selectedDays,
        showWeekdays,
        toggleDay,
        handleRepeatEverydayChange
    } = useWeekdaySelection({ shift });

    // Use keyboard navigation hook
    const {
        isInteractiveMode,
        setIsInteractiveMode,
        focusedDayIndex,
        handleContainerKeyDown,
        handleContainerClick,
        handleWeekdayKeyDown
    } = useKeyboardNavigation({
        weekdaysCount: WEEKDAYS_MAP.size,
        containerRef,
        weekdayRefs,
        showWeekdays,
        onToggleDay: toggleDay
    });

    // Use hover/focus hook for button visibility
    const {
        showIconButtons,
        handleMouseEnter,
        handleMouseLeave,
        handleFocus: handleContainerFocus,
        handleBlur: handleHoverBlur
    } = useHoverFocus();

    // Handle blur - exit interactive mode when focus leaves this ShiftBlock
    const handleBlur = React.useCallback((e: React.FocusEvent<HTMLDivElement>) => {
        handleHoverBlur(e);

        // Check if the new focus target is outside the ShiftBlock container
        if (containerRef.current && !containerRef.current.contains(e.relatedTarget as Node)) {
            setIsInteractiveMode(false);
        }
    }, [handleHoverBlur, setIsInteractiveMode]);

    // Force re-render when specific properties change that we care about
    React.useEffect(() => {
        // This effect will trigger whenever shift or its important properties change
        // The component will re-render as a result
    }, [shift, shift.openSlots, shift.title, shift.startTime, shift.endTime]);

    const getSlotDropdownOptions = memoize((minSlots: number, maxSlots: number): IDropdownOption[] => {
        let items: IDropdownOption[] = [];
        for (let i = minSlots; i <= maxSlots; i++) {
            const title = i === 1 ? _strings.get("slotsOptionLabelSingular").format(String(i)) : _strings.get("slotsOptionLabelPlural").format(String(i));
            items.push({
                key: i,
                text: title,
                ariaLabel: title
            });
        }

        return items;
    });

    const options = getSlotDropdownOptions(MINIMUM_OPEN_SHIFT_SLOTS, MAXIMUM_OPEN_SHIFT_SLOTS);

    const _onSmartScheduleSlotChange = (selectedValue: number) => {
        updateSlotsSmartScheduleShiftById(selectedValue, shift.id);
    };

    const onEditShiftBlock = (shiftId: string) => {
        let editShift = scheduleGenerationEditorState().shiftBlocks.find(shift => shift.id === shiftId);
        launchShiftEditor(
            { smartScheduleShift: editShift },
            ShiftTypes.SmartSchedule,
            null, // member
            scheduleGenerationEditorState().selectedGroupId || "", // groupTagId
            ScheduleCalendarTypes.Day,
            true, // isEditShift
            false, // isEditTimeoff
            shiftEditorViewState(),
            undefined, // startDate
            false, // isOpenShift
            true, // isSmartScheduleShift
            null // elementToFocusOnDismiss
        );
    };

    const onChangeRepeatEveryDay = (_: React.FormEvent<IToggle>, data: CheckboxOnChangeData) => {
        handleRepeatEverydayChange(!!data.checked);
    };

    const handleWeekdayClick = (day: WeekDays, index: number) => {
        toggleDay(day);
    };

    // Create aria-label for the shift block container
    const shiftAriaLabel = React.useMemo(() => {
        const shiftLabel = ShiftUtils.calculateShiftTitleAriaLabelForShiftBlock(shift);
        return `${getStrings("smartSchedule", "shiftBlock")} - ${shiftLabel}`;
    }, [shift]);

    return (
        <div className={ styles.shiftBlockContainer }
            ref={containerRef}
            aria-label={shiftAriaLabel}
            tabIndex={isInteractiveMode ? -1 : 0}
            onMouseEnter={handleMouseEnter}
            onMouseLeave={handleMouseLeave}
            onFocus={handleContainerFocus}
            onBlur={handleBlur}
            onClick={handleContainerClick}
            onKeyDown={handleContainerKeyDown}>
            <div className={ styles.shiftBlockContent } >
                <ShiftThemeContainer
                    theme={ shift.theme }
                    nonClickable={ true }
                    className={ styles.shiftThemeContainer }>
                            <Shift
                                shift={ shift } showUnsharedStatus={ false } scheduleCellRenderSize={ ScheduleCellRenderSize.Large } doShowIcons={ false } doShowNotes={ false } doShow24HrShift={ false } showGroupName={ false }/>
                </ShiftThemeContainer>
                <SlotSelector
                    tabIndex={isInteractiveMode ? 0 : -1}
                    className={styles.slotDropdown}
                    appearance="filled-darker"
                    options={options}
                    selectedSlots={shift.openSlots}
                    onSelectionChange={_onSmartScheduleSlotChange}
                    ariaLabel={getStrings("addEditShiftTimeOffPanelFullPage", "slotsFieldAriaLabel")}
                    automationId={AutomationUtils.getAutomationId("addEditShiftPanel", "QAIDOpenShiftSlots")}
                />
                <Checkbox
                    tabIndex={isInteractiveMode ? 0 : -1}
                    label={ <Text className={styles.shiftsBlockLabel}>{getStrings("smartSchedule", "repeatEveryday")}</Text> }
                    checked={ !showWeekdays }
                    onChange={ onChangeRepeatEveryDay }
                    data-automation-id={ AutomationUtils.getAutomationId("addEditShiftPanel", "QAIDRepeatOnCheckbox") }
                />
                {
                    showIconButtons &&
                        <div className={ styles.iconButtonsContainer } >
                            <Tooltip content={getStrings("smartSchedule", "deleteShiftBlock")} relationship="label">
                                <Button
                                    tabIndex={isInteractiveMode ? 0 : -1}
                                    appearance="outline"
                                    icon={<DeleteRegular/>}
                                    onClick={() => {
                                        deleteShiftBlock(shift.id);
                                        props.onDeleted?.();
                                    }}
                                    />
                            </Tooltip>
                            <Tooltip content={getStrings("smartSchedule", "editShiftBlock")} relationship="label">
                                <Button
                                    tabIndex={isInteractiveMode ? 0 : -1}
                                    appearance="outline"
                                    icon={<EditRegular />}
                                    onClick={() => onEditShiftBlock(shift.id)}/>
                            </Tooltip>
                        </div>
                }
            </div>
            {showWeekdays && <Text className={styles.shiftsBlockLabel}>{getStrings("smartSchedule", "repeatOn")}</Text>}
            {showWeekdays &&
                <div
                    className={ styles.repeatOnContainer }
                    role="toolbar"
                    aria-label={getStrings("smartSchedule", "repeatOn")}
                    aria-orientation="horizontal">
                    {Array.from(WEEKDAYS_MAP).map(([key, day], index) => {
                        const isSelected = selectedDays.includes(day);
                        const isCurrentlyFocused = focusedDayIndex === index;
                        // Only make weekday keyboard-accessible when in interactive mode
                        const weekdayTabIndex = isInteractiveMode && isCurrentlyFocused ? 0 : -1;
                        return (
                            <div
                                key={key}
                                ref={(el) => (weekdayRefs.current[index] = el)}
                                className={classNames(styles.dayOfTheWeek, isSelected ? styles.selected : styles.unselected)}
                                role="button"
                                tabIndex={weekdayTabIndex}
                                aria-pressed={isSelected}
                                aria-label={`${key}${isSelected ? ', selected' : ', not selected'}`}
                                onClick={() => handleWeekdayClick(day, index)}
                                onKeyDown={(e) => handleWeekdayKeyDown(e, index, day)}>
                                {key}
                            </div>
                        );
                    })}
                </div>
            }
        </div>
    );
};

export default observer(ShiftBlock);
